/**
 * Algolia filter bar: pins on desktop and syncs z-index with the header.
 */

class FilterBarElement extends HTMLElement {
  /** @type {number} */ #anchorTop = 0;
  /** @type {number | null} */ #pinLine = null;
  /** @type {MediaQueryList} */ #desktop = window.matchMedia('(min-width: 1024px)');
  /** @type {MutationObserver | null} */ #headerObserver = null;
  /** @type {MutationObserver | null} */ #announcementObserver = null;

  connectedCallback() {
    requestAnimationFrame(this.#update);
    window.addEventListener('scroll', this.#update, { passive: true });
    window.addEventListener('resize', this.#update, { passive: true });
    this.#desktop.addEventListener('change', this.#update);
    this.#observeHeaderGroup();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#update);
    window.removeEventListener('resize', this.#update);
    this.#desktop.removeEventListener('change', this.#update);
    this.#headerObserver?.disconnect();
    this.#announcementObserver?.disconnect();
  }

  // Observes the header group for changes to the menu-active and scrolled classes.
  #observeHeaderGroup() {
    const header = document.querySelector('header-element');
    if (header) {
      this.#headerObserver = new MutationObserver(this.#update);
      this.#headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
    }

    const announcementBar = document.querySelector('announcement-bar:not([data-menu-clone])');
    if (announcementBar) {
      this.#announcementObserver = new MutationObserver(this.#update);
      this.#announcementObserver.observe(announcementBar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  // Matches `top-sticky-filter-bar` in inset.css
  #getStickyTop() {
    const styles = getComputedStyle(document.documentElement);
    const announcementBar = document.querySelector('announcement-bar:not([data-menu-clone])');
    const stickyBehavior = announcementBar?.dataset.stickyBehavior || '';
    const announcementSticks = stickyBehavior === 'sticky' || stickyBehavior === 'semi-sticky';

    // Only sticky / semi-sticky announcement bars stay in the sticky stack on scroll up.
    const announcementVisible = announcementSticks
      ? Math.max(
          0,
          (Number.parseFloat(styles.getPropertyValue('--announcement-bar-height')) || 0) -
            (Number.parseFloat(styles.getPropertyValue('--announcement-bar-top')) || 0)
        )
      : 0;

    return (
      announcementVisible +
      (Number.parseFloat(styles.getPropertyValue('--scroll-indicator-height')) || 0) +
      (Number.parseFloat(styles.getPropertyValue('--header-height')) || 0) -
      (Number.parseFloat(styles.getPropertyValue('--header-top')) || 0)
    );
  }

  // Updates the filter bar element's state based on the header group and scroll position.
  #update = () => {
    const header = document.querySelector('header-element');
    const isDesktop = this.#desktop.matches;
    const isPinned = this.classList.contains('scrolled');

    this.toggleAttribute('data-below-navigation', header?.classList.contains('menu-active'));
    this.toggleAttribute(
      'data-above-header',
      isDesktop &&
        !header?.classList.contains('menu-active') &&
        header?.classList.contains('scrolled') &&
        !header?.classList.contains('scroll-up')
    );

    if (!isDesktop) {
      this.#setPinned(false);
      this.#syncSlotAccessibility(false);
      return;
    }

    if (!isPinned) {
      this.#anchorTop = this.getBoundingClientRect().top + window.scrollY;
    }

    const styles = getComputedStyle(document.documentElement);
    const stickyTop = this.#getStickyTop();
    const pinLead = Number.parseFloat(styles.getPropertyValue('--filter-bar-sticky-offset')) || 40;
    const naturalTop = this.#anchorTop - window.scrollY;
    const pinLine = stickyTop + pinLead;

    let shouldPin;
    if (isPinned) {
      // Keep using the pin line from when we pinned so header hide/show does not flicker state.
      shouldPin = naturalTop <= this.#pinLine + pinLead;
    } else {
      shouldPin = naturalTop <= pinLine;
      if (shouldPin) {
        this.#pinLine = pinLine;
      }
    }

    if (!shouldPin) {
      this.#pinLine = null;
    }

    this.#setPinned(shouldPin);
  };

  // Syncs the accessibility of the quick filters and grid toggle slots based on the filter bar's state.
  #syncSlotAccessibility(isPinned) {
    const quickFilters = this.querySelector('[data-filter-bar-quick-filters]');
    const gridToggle = this.querySelector('[data-filter-bar-grid-toggle]');

    if (!this.#desktop.matches) {
      quickFilters?.removeAttribute('inert');
      gridToggle?.removeAttribute('inert');
      return;
    }

    if (isPinned) {
      quickFilters?.setAttribute('inert', '');
      gridToggle?.removeAttribute('inert');
      return;
    }

    quickFilters?.removeAttribute('inert');
    gridToggle?.setAttribute('inert', '');
  }

  // Sets the filter bar element's pinned state
  #setPinned(shouldPin) {
    const isPinned = this.classList.contains('scrolled');

    if (isPinned === shouldPin) {
      return;
    }

    if (shouldPin) {
      // Snap instantly on pin, keep transition active for the same frame as header updates.
      this.style.transition = 'none';
      this.classList.add('scrolled');
      this.offsetHeight;
      this.style.removeProperty('transition');
      this.#syncSlotAccessibility(true);
      return;
    }

    // Snap instantly on unpin.
    this.style.removeProperty('transition');
    this.classList.remove('scrolled');
    this.#pinLine = null;
    this.#syncSlotAccessibility(false);
  }
}

customElements.define('filter-bar-element', FilterBarElement);
